import { Layer } from 'konva/lib/Layer'
import { Image } from 'konva/lib/shapes/Image'
import { Line } from 'konva/lib/shapes/Line'
import { Text } from 'konva/lib/shapes/Text'
import { Circle } from 'konva/lib/shapes/Circle'
import { Rect } from 'konva/lib/shapes/Rect'
import { Ellipse } from 'konva/lib/shapes/Ellipse'
import { Wedge } from 'konva/lib/shapes/Wedge'
import { Ring } from 'konva/lib/shapes/Ring'
import { Label } from 'konva/lib/shapes/Label'
import { Arc } from 'konva/lib/shapes/Arc'
import { Arrow } from 'konva/lib/shapes/Arrow'
import { Stage } from 'konva/lib/Stage'
import Promise from 'bluebird'
import unitUtil from '../utils/unit.util'
import { Markup, Element } from '../views/LabelBuilder/LabelBuilder.slice'
import barcodeService from './barcode.service'
import imageService from './image.service'

async function addElementToStage (stage: Stage, element: Element) {
  const layer = new Layer()
  switch (element.type) {
    case 'image': {
      await new Promise((resolve) => {
        imageService.generateImage(element.url, image => {
          const shape = new Image({
            id: element.id,
            image,
            width: element.width,
            height: element.height,
            x: element.x,
            y: element.y,
            scaleX: element.scaleX,
            scaleY: element.scaleY,
            rotation: element.rotation
          })
          layer.add(shape)
          resolve(true)
        })
      })
      break
    }
    case 'text': {
      const shape = new Text({
        id: element.id,
        text: element.text,
        x: element.x,
        y: element.y,
        scaleX: element.scaleX,
        scaleY: element.scaleY,
        rotation: element.rotation,
        fontFamily: element.font.family,
        fontSize: element.font.size,
        fontStyle: element.font.style,
        align: element.align,
        fill: element.font.color
      })
      layer.add(shape)
      break
    }
    case 'barcode': {
      await new Promise((resolve) => {
        barcodeService.generateBarcode(element.format, element.text, image => {
          const shape = new Image({
            id: element.id,
            image,
            width: element.width,
            height: element.height,
            x: element.x,
            y: element.y,
            scaleX: element.scaleX,
            scaleY: element.scaleY,
            rotation: element.rotation
          })
          layer.add(shape)
          resolve(true)
        })
      })
      break
    }
    case 'line': {
      const shape = new Line({
        id: element.id,
        points: element.points,
        stroke: element.stroke,
        x: element.x,
        y: element.y,
        scaleX: element.scaleX,
        scaleY: element.scaleY,
        rotation: element.rotation
      })
      layer.add(shape)
      break
    }
    case 'circle': {
      const shape = new Circle({
        id: element.id,
        radius: element.radius,
        fill: element.fill,
        stroke: element.stroke,
        strokeWidth: element.strokeWidth,
        x: element.x,
        y: element.y,
        scaleX: element.scaleX,
        scaleY: element.scaleY,
        rotation: element.rotation
      })
      layer.add(shape)
      break
    }
    case 'rect': {
      const shape = new Rect({
        id: element.id,
        fill: element.fill,
        stroke: element.stroke,
        strokeWidth: element.strokeWidth,
        x: element.x,
        y: element.y,
        scaleX: element.scaleX,
        scaleY: element.scaleY,
        rotation: element.rotation,
        width: element.width,
        height: element.height
      })
      layer.add(shape)
      break
    }
    case 'ellipse': {
      const shape = new Ellipse({
        id: element.id,
        radiusX: element.radiusX,
        radiusY: element.radiusY,
        fill: element.fill,
        stroke: element.stroke,
        strokeWidth: element.strokeWidth,
        x: element.x,
        y: element.y,
        scaleX: element.scaleX,
        scaleY: element.scaleY,
        rotation: element.rotation
      })
      layer.add(shape)
      break
    }
    case 'wedge': {
      const shape = new Wedge({
        id: element.id,
        radius: element.radius,
        fill: element.fill,
        stroke: element.stroke,
        strokeWidth: element.strokeWidth,
        x: element.x,
        y: element.y,
        scaleX: element.scaleX,
        scaleY: element.scaleY,
        rotation: element.rotation,
        angle: element.angle
      })
      layer.add(shape)
      break
    }
    case 'ring': {
      const shape = new Ring({
        id: element.id,
        fill: element.fill,
        stroke: element.stroke,
        strokeWidth: element.strokeWidth,
        x: element.x,
        y: element.y,
        scaleX: element.scaleX,
        scaleY: element.scaleY,
        rotation: element.rotation,
        outerRadius: element.outerRadius,
        innerRadius: element.innerRadius
      })
      layer.add(shape)
      break
    }
    case 'arc': {
      const shape = new Arc({
        angle: element.angle,
        outerRadius: element.outerRadius,
        innerRadius: element.innerRadius,
        id: element.id,
        fill: element.fill,
        stroke: element.stroke,
        strokeWidth: element.strokeWidth,
        x: element.x,
        y: element.y,
        scaleX: element.scaleX,
        scaleY: element.scaleY,
        rotation: element.rotation
      })
      layer.add(shape)
      break
    }
    case 'arrow': {
      const shape = new Arrow({
        id: element.id,
        fill: element.fill,
        stroke: element.stroke,
        strokeWidth: element.strokeWidth, 
        x: element.x,
        y: element.y,
        scaleX: element.scaleX,
        scaleY: element.scaleY,
        rotation: element.rotation,
        points: element.points,
        pointerLength: element.pointerLength,
        pointerWidth: element.pointerWidth
      })
      layer.add(shape)
      break
    }
    default:
      break
  }
  stage.add(layer)
}

export default {
  async toImageUri (markup: Markup) {
    const container = document.createElement('div')
    const width = unitUtil.unitToPixel(markup.stage.unit, markup.stage.width)
    const height = unitUtil.unitToPixel(markup.stage.unit, markup.stage.height)
    const stage = new Stage({
      container,
      width,
      height
    })
    await Promise.each(markup.elements, element => addElementToStage(stage, element))
    return stage.toDataURL({ pixelRatio: 3 })
  },

  async toImageElement (markup: Markup) {
    const imageUri = await this.toImageUri(markup)
    return imageService.generateImage(imageUri)
  }
}
